import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, Link } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CardHeader from '@mui/material/CardHeader'
import Card from '@mui/material/Card'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'

import VisibilityRounded from '@mui/icons-material/VisibilityRounded'

import PatientIcon from '@/components/ui/icons/Patient'
import Grid from '@mui/material/Grid'
import TextInput from '@/components/ui/inputs/TextInput'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import { useStartLocalConsultation } from '@/shared/model/useStartLocalConsultation'

export type PatientsCardProps = {
  disableShowMore?: boolean
  patients: {
    id: string
    name: string
    surname: string
    gender?: 'F' | 'M' | 'I' | 'O'
  }[]
  onTextSearch?: (text: string) => void
}

export default function PatientsCard(props: PatientsCardProps) {
  const { disableShowMore = false, patients, onTextSearch } = props
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { startLocalConsultation } = useStartLocalConsultation()

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onTextSearch?.(event.target.value)
  }

  return (
    <Card sx={{ minHeight: '100%', paddingBottom: 10 }}>
      <CardHeader
        title={t('page.home.patient.title')}
        action={
          <Button
            id="new-patient-button"
            component={Link}
            disableElevation
            to="/patients/create"
            variant="contained"
            sx={{ border: 'none', background: 'radial-gradient(circle, rgba(42,182,183,1) 0%, rgba(37,154,164,1) 100%)' }}
          >
            {t('page.home.patient.createPatientButton.label')}
          </Button>
        }
      />

      <Stack paddingX={3}>
        <Grid item>
          <TextInput fullWidth id="search-patient" onChange={handleChange} placeholder={t('page.patients.searchInput.placeholder')} label={t('page.patients.searchInput.placeholder')} InputLabelProps={{ className: 'sr-only' }} />
        </Grid>
        <Grid item>
          <Stack spacing={1} sx={{ marginTop: 2 }}>
            {patients.map((patient) => {
              return (
                <Stack
                  key={patient.id}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  marginY={0.5}
                  sx={{
                    paddingRight: 3,
                    borderRadius: 60,
                    height: 60,
                    backgroundColor: '#F9FAFC',
                  }}
                >
                  <Stack direction="row" alignItems="center">
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 60,
                        marginRight: 1,
                        borderRadius: 20,
                        height: 60,
                        backgroundColor: '#F0F9FF',
                      }}
                    >
                      <PatientIcon isFemale={patient.gender === 'F'} />
                    </Box>
                    <p>
                      {patient.name} {patient.surname}
                    </p>
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <IconButton onClick={() => navigate(`/patients/${patient.id}`)}>
                      <VisibilityRounded htmlColor="#269CA6" />
                    </IconButton>
                    <IconButton onClick={() => startLocalConsultation(patient.id)}>
                      <LocalHospitalIcon htmlColor="#269CA6" />
                    </IconButton>
                  </Stack>
                </Stack>
              )
            })}
          </Stack>
        </Grid>
      </Stack>
      {!disableShowMore && (
        <Stack justifyContent={'center'} alignItems={'center'} padding={2}>
          <Button id="load-more-patient-button" component={Link} disableElevation to="/patients" variant="outlined">
            {' '}
            {t('common:button.more.label')}
          </Button>
        </Stack>
      )}
    </Card>
  )
}
